import { ComponentType, useMemo, useEffect, useRef, useState } from 'react';
import { Layout } from '~/layouts/Layout';
import { Resource } from '~/views/Resource';
import { File } from '~/views/File';
import { History } from '~/views/History';
import { useSelector } from 'react-redux';
import { routePath } from '~/utils/constants';
import { useAppDispatch } from '~/store';
import { logout } from '~/store/user';
import { Login } from '~/views/Login';
import { oneBoxV3Api } from '~/api/oneBoxV3';
import { deleteCookie, getCookie, setCookie } from '~/utils/cookie';
import { v4 as uuidv4 } from 'uuid';
import { useLocation, useNavigate } from 'react-router-dom';
import { getParamValue } from '~/utils/helper/route';
import {
  getterIsValidLogin,
  getterOwnInfo,
  isOwnInfoPlaceholderProfile,
} from '~/store/user/userGetter';
import { Admin } from '~/views/Admin';
import { Login as AdminLogin } from '~/views/Admin/Login';
import { LatestNews } from '~/components/LatestNews/LatestNews';
import { StyleBackground } from './BaseContainer.style';
import { AdminLayout } from '~/layouts/AdminLayout';
import { LatestNewsLayout } from '~/layouts/LatestNewsLayout/LatestNewsLayout';
import {
  BaseDownloadPathSettingDialog,
  DOWNLOAD_PATH,
  TBaseDownloadPathSetting,
} from '~/components/Global/Dialog/BaseDownloadPathSettingDialog';
import { Authority, keyCookie } from '~/types/enum';
import { isWeb } from '~/utils/helper/device';
import { useCommon } from '~/utils/hook/useCommon';
import { useLazyAddQrcodeIdQuery } from '~/api/onePass';
import { TAPIResponseStatus } from '~/api/baseFetch/common.type';
import { Loading } from '~/components/Loading';
import { pageViewEvent, updateEduCode } from '~/tracking';
import { useOfflineMode } from '~/contexts/OfflineModeContext';
import { OfflineState } from '~/components/Offline';

type TrackedPath =
  | typeof routePath.resource
  | typeof routePath.file
  | typeof routePath.history
  | typeof routePath.login
  | typeof routePath.admin
  | typeof routePath.adminLogin
  | typeof routePath.latestNews;

type RouteConfig = {
  Component: ComponentType;
  pageTitle: string;
};

const routeConfig: Record<TrackedPath, RouteConfig> = {
  [routePath.resource]: { Component: Resource, pageTitle: '取得資源' },
  [routePath.file]: { Component: File, pageTitle: '我的光碟盒' },
  [routePath.history]: { Component: History, pageTitle: '下載紀錄' },
  [routePath.login]: { Component: Login, pageTitle: '登入' },
  [routePath.admin]: { Component: Admin, pageTitle: '管理後台' },
  [routePath.adminLogin]: { Component: AdminLogin, pageTitle: '管理後台登入' },
  [routePath.latestNews]: { Component: LatestNews, pageTitle: '最新消息' },
};

const getRouteConfig = (pathname: string): RouteConfig | undefined =>
  routeConfig[pathname as TrackedPath];

const PAGE_VIEW_DEBOUNCE_MS = 100;

export const BaseContainer = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { getToken, getBaseDownloadPathSetting } = useCommon();
  const { isOffline, isReady } = useOfflineMode();
  const [addQrcodeId] = useLazyAddQrcodeIdQuery();
  const token = getToken();
  const uuid = getCookie(keyCookie.ONEPASS_MOE_WEB);
  const { id } = getParamValue(search.slice(1));
  const isValidLogin = useSelector(getterIsValidLogin);
  const ownInfo = useSelector(getterOwnInfo);
  const [baseDownloadPathSetting, setBaseDownloadPathSetting] =
    useState<TBaseDownloadPathSetting>({
      isOpen: false,
      type: DOWNLOAD_PATH.SYSTEM,
      path: '',
    });
  const [isLoginFetching, setIsLoginFetching] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);

  const prevPathRef = useRef<string>('');
  useEffect(() => {
    if (isOffline) return;
    const timer = setTimeout(() => {
      const pageTitle = getRouteConfig(pathname)?.pageTitle;
      if (!pageTitle) return;
      pageViewEvent({
        page_title: pageTitle,
        page_location: pathname,
        from_path: prevPathRef.current,
      });
      prevPathRef.current = pathname;
    }, PAGE_VIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [pathname, isOffline]);

  useEffect(() => {
    if (isOffline) return;
    void (async () => {
      if (token) {
        // 全頁遮罩只在首次驗證（ownInfo 尚未回寫、仍為占位）時出現；
        // 之後的換頁改為背景重新驗證、不擋操作——身分異常者仍會在
        // 背景驗證完成後被下方檢查登出，把關不變、只是晚幾秒
        const shouldBlockUi = isOwnInfoPlaceholderProfile(ownInfo);
        if (shouldBlockUi) {
          setIsLoginFetching(true);
        }
        try {
          const ownInfoResponse = await dispatch(
            oneBoxV3Api.endpoints.getOwnInfo.initiate(void '', {
              forceRefetch: true,
            }),
          ).unwrap();
          // 桌機版也要在「我的光碟盒」檢查：NaniClub OIDC 一律導回站台根目錄，
          // 根目錄在桌機版轉到 /file。只檢查 /resource 的話，學生會帶著 token
          // 停在這頁，Header 顯示「登入」，按下去 club 因仍在登入狀態直接導回，
          // 沒有任何提示且無限來回。網頁版的 /file 會被轉到 /resource，不受影響。
          if (pathname === routePath.resource || pathname === routePath.file) {
            const authority = ownInfoResponse.data?.authority;
            if (ownInfoResponse.err !== 0 || authority === Authority.CLIENT) {
              const clubName = window.location.hostname.includes('nani.com.tw')
                ? 'NaniClub'
                : 'OneClub';

              alert(
                `NaniBox3.0 僅提供教師使用。系統目前無法確認您的教師身分，請至 ${clubName} 填寫個人資料，或改用教育雲端帳號（Open ID）登入後再試一次。`,
              );
              dispatch(logout());
            }
          }
          if (uuid) {
            // 由 OnePass 進入 OneBox3 然後用教育雲登入（僅在登入／首頁完成綁定後導向資源頁，避免蓋掉後台等路徑）
            const resId = await addQrcodeId({
              id: uuid,
              token: token,
            }).unwrap();
            if (resId.data && resId.status === TAPIResponseStatus.success) {
              if (pathname === routePath.login || pathname === routePath.home) {
                navigate(routePath.resource, { replace: true });
              }
            }
            deleteCookie(keyCookie.ONEPASS_MOE_WEB);
          }
        } catch (err) {
          console.error('[login-fetch] failed:', err);
        } finally {
          setIsLoginFetching(false);
        }
      } else {
        updateEduCode('未登入');
      }
    })();
  }, [pathname, token, uuid, isOffline]);

  useEffect(() => {
    if (isOffline) return;
    isValidLogin &&
      pathname === routePath.login &&
      !id &&
      navigate(routePath.resource, { replace: true });
  }, [pathname, isValidLogin, id, isOffline]);

  useEffect(() => {
    if (isOffline) return;
    const currentHostId = getCookie(keyCookie.ONEPASS_HOST_ID);
    if (!currentHostId) {
      setCookie(keyCookie.ONEPASS_HOST_ID, uuidv4()); // 給裝置一個 unique id
    }
  }, [isOffline]);

  useEffect(() => {
    if (!isWeb) {
      getBaseDownloadPathSetting((setting: TBaseDownloadPathSetting) => {
        setBaseDownloadPathSetting(setting);
      });
    }
  }, [isWeb, getBaseDownloadPathSetting]);

  // const BaseAction = (_updateState: keyof typeof UpdateElectronStatus) => {
  //   switch (_updateState) {
  //     case UpdateElectronStatus.error:
  //     case UpdateElectronStatus.updateNotAvailable:
  //       return (
  //         <>
  //           <BaseButton
  //             variant={BASE_BUTTON_VARIANT.OUTLINED}
  //             color={BASE_BUTTON_COLOR.PRIMARY}
  //             onClick={() => setUpdateElectronModal(false)}
  //             text="關閉"
  //           />
  //         </>
  //       );
  //     case UpdateElectronStatus.checking:
  //     case UpdateElectronStatus.downloading:
  //       return null;
  //     case UpdateElectronStatus.updateAvailable:
  //       return (
  //         <>
  //           <BaseButton
  //             variant={BASE_BUTTON_VARIANT.OUTLINED}
  //             color={BASE_BUTTON_COLOR.PRIMARY}
  //             onClick={() => setUpdateElectronModal(false)}
  //             text="下次再說"
  //           />
  //           <BaseButton
  //             variant={BASE_BUTTON_VARIANT.FILLED}
  //             color={BASE_BUTTON_COLOR.PRIMARY}
  //             onClick={() => isDownload()}
  //             text="確定"
  //           />
  //         </>
  //       );
  //     case UpdateElectronStatus.downloadCompleted:
  //     case UpdateElectronStatus.updated:
  //       return (
  //         <>
  //           <BaseButton
  //             variant={BASE_BUTTON_VARIANT.OUTLINED}
  //             color={BASE_BUTTON_COLOR.PRIMARY}
  //             onClick={() => setUpdateElectronModal(false)}
  //             text="關閉"
  //           />
  //           <BaseButton
  //             variant={BASE_BUTTON_VARIANT.FILLED}
  //             color={BASE_BUTTON_COLOR.PRIMARY}
  //             onClick={() => isInstall()}
  //             text="安裝"
  //           />
  //         </>
  //       );
  //   }
  // };

  const children = useMemo(() => {
    if (isOffline) {
      if (!isReady) return null;
      if (pathname !== routePath.file) {
        // 取得資源頁線上有站台頁尾，設計稿在離線狀態下也保留；
        // 下載紀錄頁線上本來就沒有頁尾，維持一致不加。
        return <OfflineState withFooter={pathname === routePath.resource} />;
      }
    }

    const Component = getRouteConfig(pathname)?.Component;
    return Component ? <Component /> : null;
  }, [pathname, isOffline, isReady]);

  return (
    <>
      {/* <Loading open={isLoading} /> */}
      <StyleBackground pathname={pathname} />
      <BaseDownloadPathSettingDialog
        open={baseDownloadPathSetting.isOpen}
        setSetting={setBaseDownloadPathSetting}
        path={baseDownloadPathSetting.path}
        type={baseDownloadPathSetting.type}
      />
      {isOffline ? (
        <Layout>{children}</Layout>
      ) : pathname === routePath.latestNews ? (
        <LatestNewsLayout>{children}</LatestNewsLayout>
      ) : pathname === routePath.admin || pathname === routePath.adminLogin ? (
        <AdminLayout>{children}</AdminLayout>
      ) : (
        <Layout>{children}</Layout>
      )}
      {/* {updateText && (
        <BaseDialog
          open={updateElectronModal}
          handleClose={() => setUpdateElectronModal(false)}
          action={BaseAction(updateState)}
          title="更新 NaniBox 3.0"
        >
          {updateProgress > 0 && (
            <LinearProgress variant="determinate" value={updateProgress} />
          )}
          <Typography
            variant="body1"
            sx={{ maxWidth: '600px', minWidth: '500px' }}
          >
            {updateText}
          </Typography>
        </BaseDialog>
      )} */}
      <Loading open={isLoginFetching} />
    </>
  );
};
